import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/utils/shared";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-100 [&_svg]:pointer-events-none [&_svg]:shrink-0 hover:cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "bg-primary-600 text-primary-foreground hover:bg-primary-500 active:bg-primary-700 focus-visible:bg-primary-700 focus-visible:ring-3 focus-visible:ring-primary-200 disabled:bg-primary-300",
        destructive:
          "bg-destructive text-white focus-visible:ring-3 focus-visible:ring-error-300 disabled:bg-error-300",
        "destructive-outline":
          "border border-error-400 bg-neutral-0 text-error-400 hover:text-error-400 hover:border-error-400 active:text-error-400 active:border-error-400 focus-visible:ring-0 focus-visible:border-error-400",
        outlineDestructive:
          "border border-destructive bg-neutral-0 text-destructive focus-visible:ring-3 focus-visible:ring-error-300",
        outline:
          "border border-primary-600 bg-transparent text-primary-600 hover:text-primary-500 hover:border-primary-500 active:text-primary-700 active:border-primary-700 focus-visible:ring-3 focus-visible:ring-primary-200 focus-visible:text-primary-700 focus-visible:border-primary-700",
        secondary:
          "bg-neutral-0 text-neutral-700 border border-neutral-300 hover:text-neutral-600 active:border-neutral-400 focus-visible:ring-3 focus-visible:ring-neutral-200 disabled:text-neutral-300",
        outlineSecondary:
          "bg-neutral-0 text-primary-600 border border-primary-600 hover:text-primary-600 active:border-primary-700 focus-visible:ring-3 focus-visible:ring-primary-200 disabled:text-primary-300",
        primary:
          "bg-primary-600 text-primary-foreground hover:bg-primary-500 active:bg-primary-700 focus-visible:bg-primary-700 focus-visible:ring-3 focus-visible:ring-primary-200 disabled:bg-primary-300",
        soft: "bg-[#816DD7]/15 text-primary-600 hover:bg-[#816DD7]/20 active:bg-[#816DD7]/30 focus-visible:bg-[#816DD7]/15 focus-visible:ring-3 focus-visible:ring-primary-100 disabled:bg-primary-100 disabled:text-primary-400",
        link: "text-primary-600 hover:text-primary-500 active:text-primary-700 focus-visible:text-primary-700 focus-visible:ring-0 disabled:text-primary-300",
        nightfall: "bg-nightfall text-neutral-0",
        outlineNightfall:
          "rounded-[11.5px]! bg-neutral-0 text-primary-600 hover:bg-nightfall-50 active:bg-nigthtfall-80 focus-visible:ring-0 focus-visible:border-none",
        alba: "bg-alba text-neutral-0 relative overflow-hidden after:pointer-events-none after:content-[''] after:absolute after:inset-0 after:rounded-xl after:bg-[linear-gradient(135deg,rgba(255,255,255,0.35)_0%,rgba(255,255,255,0.18)_40%,rgba(255,255,255,0)_100%)] after:opacity-0 after:transition-opacity after:duration-300 hover:after:opacity-100",
        ghost: "hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "h-8 px-4 py-2.5 text-sm [&_svg]:size-4",
        sm: "h-8 text-sm px-6 [&_svg]:size-4",
        md: "h-9 text-base [&_svg]:size-5",
        lg: "h-10 text-base [&_svg]:size-5",
        xl: "h-11 text-base [&_svg]:size-5",
        icon: "h-9 w-9",
        "pagination-number": "h-7 w-7",
      },
      // This prop, although available in the component API is meant just for internal use
      // and would be overridden by the component itself
      padding: {
        "sm-just-text": "px-4 py-2.5",
        "md-just-text": "px-5 py-2.5",
        "lg-just-text": "px-5 py-2.5",
        "xl-just-text": "px-6 py-2.5",

        "sm-icon-plus-text": "pl-3 pr-4 py-2.5",
        "md-icon-plus-text": "pl-3 pr-5 py-2.5",
        "lg-icon-plus-text": "pl-3 pr-5 py-2.5",
        "xl-icon-plus-text": "pl-4 pr-6 py-2.5",

        "sm-just-icon": "p-2.5",
        "md-just-icon": "p-2.5",
        "lg-just-icon": "p-2.5",
        "xl-just-icon": "p-2.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isJustIcon?: boolean;
  isIconPlusText?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild = false,
      children,
      className,
      isJustIcon = false,
      isIconPlusText = false,
      loading = false,
      size,
      variant,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    const sizeForPadding = size || "sm";
    let padding = `${sizeForPadding}-`;
    if (isJustIcon) {
      padding += "just-icon";
    }
    if (!isJustIcon && isIconPlusText) {
      padding += "icon-plus-text";
    }
    if (!isJustIcon && !isIconPlusText) {
      padding += "just-text";
    }

    const buttonClassName =
      variant === "outlineNightfall"
        ? cn(
            "p-px bg-nightfall rounded-xl focus-visible:ring-3 focus-visible:border-none focus-visible:bg-nightfall focus-visible:ring-primary-200",
            "disabled:opacity-100 disabled:pointer-events-none w-full md:w-auto",
            className
          )
        : cn(
            buttonVariants({
              className,
              size,
              variant,
              padding: padding as ButtonProps["padding"],
            })
          );

    return (
      <Comp className={buttonClassName} ref={ref} disabled={loading || props.disabled} {...props}>
        {variant === "outlineNightfall" ? (
          <div
            className={cn(
              buttonVariants({
                className,
                size,
                variant,
                padding: padding as ButtonProps["padding"],
              })
            )}
          >
            {!loading && children}
            {loading &&
              Array.from({ length: 3 }, (_, i) => (
                <div
                  className="w-[6px] h-[6px] rounded bg-[currentColor]"
                  style={{
                    animation: "bounce 0.8s ease-in-out infinite",
                    animationDelay: `${i * 150}ms`,
                  }}
                  key={i}
                />
              ))}{" "}
          </div>
        ) : (
          <>
            {!loading && children}
            {loading &&
              Array.from({ length: 3 }, (_, i) => (
                <div
                  className="w-[6px] h-[6px] rounded bg-[currentColor]"
                  style={{
                    animation: "bounce 0.8s ease-in-out infinite",
                    animationDelay: `${i * 150}ms`,
                  }}
                  key={i}
                />
              ))}
          </>
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };

<style jsx={true}>{`
  @keyframes bounce {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-8px);
    }
  }
`}</style>;
